import dayjs from "dayjs";
import { APP_SITE_ID, resolveCanonicalPublicMailingUrl } from "../../config/appSiteLinks";
import { firstNameOrFallback } from "./PromotionEmailHtml";

export const DRAFT_RECIPIENTS_DEFAULT = [{ email: "syc0701@gmail.com", name: "Youngcheol" }];
export const LOG_SOURCE = "PromotionEmail";
export const STATUS_DRAFT = "DRAFT";
export const STATUS_READY_TO_SENT = "READY_TO_SENT";
export const STATUS_SENT = "SENT";
export const STAGE_TEST = "TEST";
export const STAGE_PROD = "PROD";
export const STAGE_OPTIONS = [STAGE_TEST, STAGE_PROD];
export const DEFAULT_APP = APP_SITE_ID.JIGSAW_MUSE;
export const PROMOTION_APPS = [
    { id: APP_SITE_ID.WEB, label: "WEB" },
    { id: APP_SITE_ID.JIGSAW_MUSE, label: "Jigsaw Muse" },
    { id: APP_SITE_ID.WORD_WHEEL_QUEST, label: "Word Wheel Quest" },
    { id: APP_SITE_ID.SLIDING_PUZZLE_QUEST, label: "Sliding Puzzle Quest" },
];

const PROMOTION_APP_STORAGE_KEY = "admin.advertisement.promotion.app";

export function isPromotionAppId(value) {
    return PROMOTION_APPS.some((option) => option.id === value);
}

export function readStoredPromotionApp() {
    try {
        const stored = window.localStorage.getItem(PROMOTION_APP_STORAGE_KEY);
        if (isPromotionAppId(stored)) return stored;
    } catch {
        /* private mode */
    }
    return null;
}

export function loadStoredPromotionApp() {
    return readStoredPromotionApp() || DEFAULT_APP;
}

export function persistPromotionApp(appId) {
    if (!isPromotionAppId(appId)) return;
    try {
        window.localStorage.setItem(PROMOTION_APP_STORAGE_KEY, appId);
    } catch {
        /* private mode */
    }
}

export function statusChipColor(status) {
    if (status === STATUS_READY_TO_SENT) return "warning";
    if (status === STATUS_SENT) return "success";
    return "default";
}

export function stageChipColor(stage) {
    return stage === STAGE_PROD ? "success" : "warning";
}

export function normalizeStage(value) {
    return value === STAGE_PROD ? STAGE_PROD : STAGE_TEST;
}

export function normalizeStatus(value) {
    return value === "READY" ? STATUS_DRAFT : (value || STATUS_DRAFT);
}

export function previewUnsubscribeUrl(status, draftRecipients, readyRecipients) {
    let email = DRAFT_RECIPIENTS_DEFAULT[0].email;
    if (status === STATUS_READY_TO_SENT) {
        const first = (readyRecipients || []).find((r) => r?.email);
        if (first?.email) email = first.email;
    }
    return resolveCanonicalPublicMailingUrl("/email/unsubscribe", email);
}

export function previewRecipientName(status, draftRecipients, readyRecipients, result) {
    if (status === STATUS_READY_TO_SENT) {
        const first = (readyRecipients || []).find((r) => r?.name || r?.email);
        return firstNameOrFallback(first?.name || first?.email);
    }
    if (status === STATUS_SENT) {
        const first = (result || []).find((r) => r?.name || r?.email);
        if (first) return firstNameOrFallback(first.name || first.email);
    }
    const draft = (draftRecipients || [])[0];
    return firstNameOrFallback(draft?.name || draft?.email || "Youngcheol");
}

export function todayNamePrefix() {
    return `${dayjs().format("YYMMDD")}-`;
}

export function stripDateNamePrefix(value) {
    const text = typeof value === "string" ? value.trim() : "";
    return text.replace(/^\d{6}-/, "");
}

export function uniqueRecipientsByEmail(list) {
    const seen = new Set();
    const out = [];
    for (const row of list || []) {
        const email = typeof row?.email === "string" ? row.email.trim().toLowerCase() : "";
        if (!email || seen.has(email)) continue;
        seen.add(email);
        out.push(row);
    }
    return out;
}

export function newPromotionCode(app = DEFAULT_APP) {
    return `${app}_${dayjs().format("YYMMDD_HHmmss")}`;
}

export function promotionAppLabel(appId) {
    if (!appId) return "";
    const match = PROMOTION_APPS.find((option) => option.id === appId);
    return match?.label || String(appId);
}

export function logAppLabel(row, promotions) {
    const fromDetails = row?.details?.app;
    if (fromDetails) {
        return promotionAppLabel(fromDetails) || "—";
    }
    const id = row?.details?.promotionEmailId;
    if (!id || !Array.isArray(promotions)) return "—";
    const promo = promotions.find((p) => p.id === id || String(p.id) === String(id));
    return promotionAppLabel(promo?.app) || "—";
}

export function logEmailLabel(row) {
    const details = row?.details;
    if (!details || typeof details !== "object") return "—";
    if (details.email) return String(details.email);
    const recipients = details.recipients;
    if (!Array.isArray(recipients) || recipients.length === 0) return "—";
    return recipients
        .map((item) => (typeof item === "string" ? item : item?.email))
        .filter(Boolean)
        .join(", ");
}

export function logTitleLabel(row) {
    const details = row?.details;
    if (!details || typeof details !== "object") return "—";
    if (details.title) return String(details.title);
    if (Array.isArray(details.sentCodes) && details.sentCodes.length) {
        return details.sentCodes.join(", ");
    }
    return "—";
}

export function isIdleSchedulerLog(row) {
    const msg = typeof row?.message === "string" ? row.message : "";
    return (
        msg.startsWith("Scheduled promotion send started")
        || msg.startsWith("Scheduled promotion send skipped")
        || msg.startsWith("Scheduled promotion send finished with no emails sent")
    );
}

export function applyPromotionToForm(promo, setters) {
    const {
        setName,
        setSubject,
        setCode,
        setStatus,
        setApp,
        setHtml,
        setLogo,
        setDraftRecipients,
        setReadyRecipients,
        setResult,
        setStage,
    } = setters;
    if (promo.code) setCode(promo.code);
    if (promo.name) setName(promo.name);
    if (promo.subject != null) setSubject(promo.subject);
    setStatus(normalizeStatus(promo.status));
    setApp(promo.app || DEFAULT_APP);
    setHtml(promo.htmlContent || "");
    setLogo(promo.logo || "");
    setDraftRecipients(
        Array.isArray(promo.draftRecipients) && promo.draftRecipients.length
            ? promo.draftRecipients
            : DRAFT_RECIPIENTS_DEFAULT
    );
    setReadyRecipients(uniqueRecipientsByEmail(Array.isArray(promo.readyRecipients) ? promo.readyRecipients : []));
    setResult(Array.isArray(promo.result) ? promo.result : []);
    setStage(normalizeStage(promo.stage));
}

export function buildDuplicatePayload(promo) {
    const app = promo?.app || DEFAULT_APP;
    const rest = stripDateNamePrefix(promo?.name);
    return {
        code: newPromotionCode(app),
        name: `${todayNamePrefix()}${rest || "copy"}`,
        app,
        subject: promo?.subject || "",
        content: promo?.htmlContent || promo?.content || "",
        logo: promo?.logo || undefined,
        status: STATUS_DRAFT,
        stage: normalizeStage(promo?.stage),
        draftRecipients: DRAFT_RECIPIENTS_DEFAULT,
        readyRecipients: uniqueRecipientsByEmail(Array.isArray(promo?.readyRecipients) ? promo.readyRecipients : []),
    };
}
